import { UIRouterModule } from "@uirouter/angular";
import { A11yModule } from "@angular/cdk/a11y";
import { OverlayModule } from "@angular/cdk/overlay";
import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AssigneesModule } from "@webapp/assignees/assignees.module";
import { CommentsModule, InitialMentionsService } from "@webapp/comments";
import { LocalizationModule } from "@webapp/localization/localization.module";
import { HasPermissionsDirective } from "@webapp/permissions/directives/has-permissions.directive";
import { ExpandableContentModule } from "@webapp/shared/components/expandable-content/expandable-content.module";
import { FormattedValueModule } from "@webapp/shared/components/formatted-value/formatted-value.module";
import { IconWithLabelModule } from "@webapp/shared/components/icon-with-label/icon-with-label.module";
import { OnboardingModule } from "@webapp/shared/components/onboarding/onboarding.module";
import { ProgressIndicatorLabelModule } from "@webapp/shared/components/progress-indicator-label/progress-indicator-label.module";
import { DropdownModule } from "@webapp/shared/dropdown/dropdown.module";
import { FormModule } from "@webapp/shared/form/form.module";
import { SharedModalModule } from "@webapp/shared/modal/shared-modal.module";
import { RichTextEditorWithMentionsModule } from "@webapp/shared/rich-text-editor-with-mentions/rich-text-editor-with-mentions.module";
import { UiAccessibilityModule } from "@webapp/ui/accessibility/accessibility.module";
import { UiBadgeModule } from "@webapp/ui/badge/badge.module";
import { UiButtonModule } from "@webapp/ui/button/button.module";
import { UiDatePickerModule } from "@webapp/ui/date-picker/date-picker.module";
import { UiDrawerModule } from "@webapp/ui/drawer/drawer.module";
import { UiFormModule } from "@webapp/ui/form/form.module";
import { UiGridModule } from "@webapp/ui/grid/grid.module";
import { UiIconModule } from "@webapp/ui/icon/icon.module";
import { UiInputNumberModule } from "@webapp/ui/input-number/input-number.module";
import { UiInputModule } from "@webapp/ui/input/input.module";
import { UiListModule } from "@webapp/ui/list/list.module";
import { UiModalModule } from "@webapp/ui/modal/modal.module";
import { UiPopoverModule } from "@webapp/ui/popover/popover.module";
import { UiRadioModule } from "@webapp/ui/radio/radio.module";
import { UiSkeletonModule } from "@webapp/ui/skeleton/skeleton.module";
import { UiTooltipModule } from "@webapp/ui/tooltip/tooltip.module";
import { AddFirstCommentComponent } from "./components/kpi-comments/add-first-comment/add-first-comment.component";
import { KpiCommentFormComponent } from "./components/kpi-comments/kpi-comment-form/kpi-comment-form.component";
import { KpiCommentComponent } from "./components/kpi-comments/kpi-comment/kpi-comment.component";
import { KpiCommentsSkeletonComponent } from "./components/kpi-comments/kpi-comments-skeleton/kpi-comments-skeleton.component";
import { KpiCommentsComponent } from "./components/kpi-comments/kpi-comments/kpi-comments.component";
import { LastReplyOnPipe } from "./components/kpi-comments/kpi-comments/thread-footer/pipes/last-reply-on.pipe";
import { RepliesCountPipe } from "./components/kpi-comments/kpi-comments/thread-footer/pipes/replies-count.pipe";
import { ThreadFooterComponent } from "./components/kpi-comments/kpi-comments/thread-footer/thread-footer.component";
import { ChartPointMenuComponent } from "./components/kpi-details/kpi-chart/chart-point-menu/chart-point-menu.component";
import { KpiChartComponent } from "./components/kpi-details/kpi-chart/kpi-chart.component";
import { KpiDetailsSkeletonComponent } from "./components/kpi-details/kpi-details-skeleton/kpi-details-skeleton.component";
import { KpiDetailsComponent } from "./components/kpi-details/kpi-details.component";
import { KpiProgressComponent } from "./components/kpi-details/kpi-progress/kpi-progress.component";
import { KpiStatisticsCardComponent } from "./components/kpi-details/kpi-statistics/kpi-statistics-card.component";
import { KpiStatisticsListComponent } from "./components/kpi-details/kpi-statistics/kpi-statistics-list.component";
import { KpiStatisticsApiService } from "./components/kpi-details/kpi-statistics/services/kpi-statistics-api.service";
import { KpiStatisticsFacade } from "./components/kpi-details/kpi-statistics/services/kpi-statistics-facade.service";
import { KpiTimeFiltersComponent } from "./components/kpi-details/kpi-time-filters/kpi-time-filters.component";
import { KpiOptionsDropdownComponent } from "./components/kpi-options-dropdown/kpi-options-dropdown.component";
import { KpiProjectionFormComponent } from "./components/kpi-projection-form/kpi-projection-form.component";
import { KpiSnapshotFormComponent } from "./components/kpi-snapshot-form/kpi-snapshot-form.component";
import { KpiSnapshotModalService } from "./components/kpi-snapshot-form/kpi-snapshot-modal.service";
import { KpisRoutingModule } from "./kpis-routing.module";
import { KpiCommentsOnboardingService } from "./onboarding/kpi-comments-onboarding.service";
import { KpisOnboardingService } from "./onboarding/kpis-onboarding.service";

@NgModule({
  imports: [
    FormsModule,
    CommonModule,
    A11yModule,
    KpisRoutingModule,
    UIRouterModule,
    LocalizationModule,
    AssigneesModule,
    DropdownModule,
    OnboardingModule,
    ProgressIndicatorLabelModule,
    UiGridModule,
    UiButtonModule,
    UiIconModule,
    UiButtonModule,
    UiInputModule,
    UiButtonModule,
    UiRadioModule,
    UiInputNumberModule,
    UiDatePickerModule,
    UiPopoverModule,
    UiFormModule,
    UiListModule,
    UiSkeletonModule,
    UiModalModule,
    FormModule,
    UiTooltipModule,
    ExpandableContentModule,
    IconWithLabelModule,
    FormattedValueModule,
    DropdownModule,
    CommentsModule,
    RepliesCountPipe,
    LastReplyOnPipe,
    UiAccessibilityModule,
    OverlayModule,
    UiDrawerModule,
    UiBadgeModule,
    RichTextEditorWithMentionsModule,
    SharedModalModule,
    HasPermissionsDirective,
  ],
  providers: [KpiSnapshotModalService, KpisOnboardingService, KpiCommentsOnboardingService, KpiStatisticsApiService, KpiStatisticsFacade, InitialMentionsService],
  declarations: [
    KpiDetailsComponent,
    KpiTimeFiltersComponent,
    KpiChartComponent,
    KpiStatisticsListComponent,
    KpiStatisticsCardComponent,
    KpiProjectionFormComponent,
    KpiSnapshotFormComponent,
    KpiDetailsSkeletonComponent,
    KpiProgressComponent,
    KpiOptionsDropdownComponent,
    KpiCommentsComponent,
    KpiCommentComponent,
    ChartPointMenuComponent,
    AddFirstCommentComponent,
    ThreadFooterComponent,
    KpiCommentsSkeletonComponent,
    KpiCommentFormComponent,
  ],
})
export class KpisModule {}
